import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layers, PackageSearch, Truck, ArrowRight } from 'lucide-react';
import { SERVICES } from '../constants/testIds';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1 } }),
};

export const Services = () => (
  <section id="services" data-testid={SERVICES.section} className="relative bg-brand-surface py-24 sm:py-32 border-y border-brand-border/60">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <p className="font-mono text-xs uppercase tracking-widest text-brand-amber mb-3">Full-Service Logistics</p>
      <h2 className="font-display font-extrabold uppercase tracking-tight text-brand-text text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
        We Handle Sorting, Packing &amp; Freight
      </h2>
      <Link to="/services" className="mt-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-brand-orange hover:text-brand-orangeHover transition-colors">
        See the full process <ArrowRight className="w-3.5 h-3.5" />
      </Link>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-2 gap-5">
        <motion.div
          custom={0}
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          data-testid={SERVICES.cardTripling}
          className="relative rounded-2xl border border-brand-border overflow-hidden lg:col-span-2 lg:row-span-2 min-h-[22rem] group"
        >
          <img
            src="/images/brand/warehouse-aisle-yellow.jpg"
            alt="BulkUsedTires.com warehouse aisles of sorted, high-density packed tires"
            className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/60 to-transparent" />
          <div className="relative z-10 h-full flex flex-col justify-end p-7 sm:p-9">
            <Layers className="w-8 h-8 text-brand-orange mb-4" />
            <h3 className="font-display font-bold text-2xl sm:text-3xl text-brand-text uppercase">High-Density Tripling &amp; Packing</h3>
            <p className="mt-3 text-brand-muted text-sm sm:text-base max-w-md">
              Specialized tripling techniques nest up to three tires inside one another, cutting your per-unit shipping cost by up to 60%.
            </p>
          </div>
        </motion.div>

        <motion.div
          custom={1}
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          data-testid={SERVICES.cardSorting}
          className="rounded-2xl border border-brand-border bg-brand-surface2 p-7 sm:p-8 min-h-[13rem] flex flex-col"
        >
          <PackageSearch className="w-7 h-7 text-brand-amber mb-4" />
          <h3 className="font-display font-bold text-xl sm:text-2xl text-brand-text uppercase">Rigorous Sorting &amp; Grading</h3>
          <p className="mt-2 text-brand-muted text-sm sm:text-base">
            Manual + laser tread grading guarantees consistent quality across every container.
          </p>
        </motion.div>

        <motion.div
          custom={2}
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          data-testid={SERVICES.cardFreight}
          className="rounded-2xl border border-brand-border bg-brand-surface2 p-7 sm:p-8 min-h-[13rem] flex flex-col"
        >
          <Truck className="w-7 h-7 text-brand-green mb-4" />
          <h3 className="font-display font-bold text-xl sm:text-2xl text-brand-text uppercase">Freight &amp; Container Shipping</h3>
          <p className="mt-2 text-brand-muted text-sm sm:text-base">
            Full 20ft &amp; 40ft HC container loading coordinated through our worldwide partner network, with complete documentation.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);
